import { useLayoutEffect, useMemo, useRef } from 'react';

import * as Mailcheck from 'mailcheck';
import { AsYouType, parsePhoneNumberFromString } from 'libphonenumber-js';
import isEmail from 'validator/lib/isEmail';

import { TextInputBaseProps } from '../shared/TextInputBase';
import { ValidatedTextInputProps } from './ValidatedTextInput';

/**
 * Validate input values
 */
export const validateInputValue = (
  handleValidationChange: (value: string | undefined) => void,
  value: NonNullable<TextInputBaseProps['value']>,
  type: ValidatedTextInputProps['format'],
  required: boolean,
  customErrorMessages?: ValidatedTextInputProps['customErrorMessages'],
) => {
  const errorMessages = {
    fieldRequired: customErrorMessages?.fieldRequired ?? 'This field is required',
    invalidEmail: customErrorMessages?.invalidEmail ?? 'Please enter a valid email address',
    invalidPhoneNumber:
      customErrorMessages?.invalidPhoneNumber ?? 'Please enter a valid phone number',
  };

  // If input empty and required, return an error message
  if (value === '' && required) {
    handleValidationChange(errorMessages.fieldRequired);
  } else if (type === 'emailAddress' && value.length > 0) {
    const isEmailValid = validateEmail(value) ? errorMessages.invalidEmail : undefined;
    handleValidationChange(isEmailValid);
  } else if (type === 'telephoneNumber' && value.length > 0) {
    const isPhoneValid = validatePhoneNumber(value) ? errorMessages.invalidPhoneNumber : undefined;
    handleValidationChange(isPhoneValid);
  } else {
    handleValidationChange(undefined);
  }
};

const BLACKLISTED_TLDS = ['como', 'comp', 'con', 'met', 'ner', 'vom'];

const validateEmail = (value: NonNullable<TextInputBaseProps['value']>): boolean => {
  const tld = value.split('.').pop();
  const blackListed = tld ? BLACKLISTED_TLDS.includes(tld) : false;
  return !isEmail(value) || blackListed;
};

const validatePhoneNumber = (value: NonNullable<TextInputBaseProps['value']>): boolean => {
  const phone = parsePhoneNumberFromString(value, 'US');
  return !phone?.isValid();
};

/**
 * Formats Input Values
 */
export const formatInputValue = (
  value: NonNullable<TextInputBaseProps['value']>,
  type: ValidatedTextInputProps['format'],
  getValue?: ValidatedTextInputProps['getValue'],
) => {
  // unformatted value is for sending back to the server and formatted is for display
  let unformattedValue = value;
  let formattedValue = value;
  if (type === 'percentage') {
    // clampPercentage restricts the user from entering invalid percentage
    // values but doesn't add invalid text content to the input (no commas,
    // etc.) so unformatted value is the same as formattedValue
    formattedValue = clampPercentage(value);
    unformattedValue = formattedValue;
  }
  if (type === 'number' || type === 'dollars') {
    unformattedValue = unformatNumber(value);
    formattedValue = formatNumber(value);
  }
  if (type === 'telephoneNumber') {
    unformattedValue = unFormatPhoneNumber(value);
    formattedValue = formatPhoneNumber(value);
  }
  // allows the client to gain access to the formatted and unformatted value of the input
  getValue && getValue({ formattedValue, unformattedValue });
  return formattedValue;
};

const clampPercentage = (text: string): string => {
  const digits = text.replace(/\D/g, '');
  if (digits.startsWith('0')) {
    return '0';
  }
  if (digits.startsWith('100')) {
    return '100';
  }
  return digits.substring(0, 2);
};

const unformatNumber = (value: string): string => {
  return value.replace(/\D/g, '');
};

// Max digit length that Intl.NumberFormat can handle is 15
const MAX_NUMBER_LENGTH = 15;
const MAX_NUMBER_LENGTH_WITH_COMMAS = 19;

const formatNumber = (dollars: string): string => {
  const digits = dollars.replace(/\D/g, '').slice(0, MAX_NUMBER_LENGTH);
  if (digits === '') {
    return digits;
  }
  const value = parseInt(digits, 10);
  return new Intl.NumberFormat('en-US').format(value);
};

const unFormatPhoneNumber = (value: string): string => {
  return value.replace(/\D/g, '');
};

const formatPhoneNumber = (value: string): string => {
  if (!(value[0] === '(' && value.length <= 4)) {
    /**
     * AsYouType format phone number into 3 ways:
     * number with + area code: +x xxx xxx xxxx.
     * number with area code: x (xxx) xxx-xxxx.
     * number without area code: (xxx) xxx-xxxx.
     */
    value = new AsYouType('US').input(value);
  }
  return value;
};

/**
 * Provides input suggestions
 * - Currently only for email
 */
export const mailcheckDefaultDomains = [
  // Keep in sync with app/services/BriteVerifyService
  'yahoo.com',
  'google.com',
  'hotmail.com',
  'gmail.com',
  'me.com',
  'aol.com',
  'mac.com',
  'live.com',
  'comcast.net',
  'googlemail.com',
  'msn.com',
  'facebook.com',
  'verizon.net',
  'sbcglobal.net',
  'att.net',
  'mail.com',
  'outlook.com',
  'icloud.com',
  'cox.net',
];

const mailcheckDefaultTopLevelDomains = ['com', 'net', 'org', 'info', 'edu'];

const suggestEmail = (email: string): Promise<string> => {
  return new Promise((resolve) => {
    Mailcheck.run({
      domains: mailcheckDefaultDomains,
      email,
      empty: () => resolve(''),
      suggested: (suggestion: MailcheckModule.ISuggestion) => {
        if (suggestion && suggestion.domain.includes('.')) {
          resolve(JSON.stringify(suggestion.full).replace(/"/g, ''));
        } else {
          resolve('');
        }
      },
      topLevelDomains: mailcheckDefaultTopLevelDomains,
    });
  });
};

export const getSuggestion = async (
  value: NonNullable<TextInputBaseProps['value']>,
  type: ValidatedTextInputProps['format'],
): Promise<string | undefined> => {
  if (type === 'emailAddress') {
    return await suggestEmail(value);
  }
  return undefined;
};

export const getFormatProps = ({
  format,
  value,
}: Pick<ValidatedTextInputProps, 'format'> & Pick<TextInputBaseProps, 'value'>): Partial<
  Pick<
    TextInputBaseProps,
    'textContentType' | 'keyboardType' | 'maxLength' | 'inputMode' | 'suffix' | 'prefix'
  >
> => {
  switch (format) {
    case 'none':
      return {
        textContentType: 'none',
        keyboardType: 'default',
      };
    case 'emailAddress':
      return {
        textContentType: 'emailAddress',
        keyboardType: 'email-address',
      };
    case 'telephoneNumber':
      return {
        textContentType: 'telephoneNumber',
        keyboardType: 'phone-pad',
        maxLength: 16,
      };
    case 'password':
      return {
        textContentType: 'password',
        keyboardType: 'default',
      };
    case 'percentage':
      return {
        inputMode: 'numeric',
        suffix: value && value.length > 0 ? '%' : undefined,
        /**
         * Note: There's currently an issue with React Native where controlled
         * inputs can cause flickering if you type really quickly because the
         * native text value is set before the JS state. Setting a precise
         * maxLength seems to help.  If we encounter issues in the future, we
         * might consider setting the native text value ourselves.
         * See: https://github.com/facebook/react-native/issues/23578#issuecomment-1903470830
         */
        maxLength:
          value && value[0] === '1' && value[1] === '0' ? 3 : value && value[0] === '0' ? 1 : 2,
      };
    case 'number':
      return {
        inputMode: 'numeric',
        maxLength: MAX_NUMBER_LENGTH_WITH_COMMAS,
      };
    case 'dollars':
      return {
        inputMode: 'numeric',
        prefix: '$',
        maxLength: MAX_NUMBER_LENGTH_WITH_COMMAS,
      };
  }
};

/**
 * Provides a debounced function callback as a hook
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const useDebounce = (callback: (...args: any) => void, delay: number) => {
  const callbackRef = useRef(callback);

  useLayoutEffect(() => {
    callbackRef.current = callback;
  });

  let timer: ReturnType<typeof setTimeout>;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const naiveDebounce = (func: (...args: any) => void, delayMs: number, ...args: any) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      func(...args);
    }, delayMs);
  };

  return useMemo(
    () =>
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (...args: any) =>
        naiveDebounce(callbackRef.current, delay, ...args),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [delay],
  );
};
